import { formatDistanceToNow, intlFormat, Locale } from 'date-fns'
import { enCA, fr } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'

const localeMap: Record<string, Locale> = {
  en: enCA,
  fr: fr,
}

export function useLocalizedDate() {
  const { i18n } = useTranslation()

  const formatHour = (date: Date) => {
    const currentLocale = i18n.language || 'en-CA'

    return intlFormat(
      date,
      {
        hour: 'numeric',
        minute: 'numeric',
      },
      {
        locale: currentLocale,
      },
    )
  }

  const formatDate = (date: Date) => {
    const currentLocale = i18n.language || 'en-CA'

    return intlFormat(
      date,
      {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      },
      {
        locale: currentLocale,
      },
    )
  }

  const formatDateWithTime = (date: Date) => {
    const currentLocale = i18n.language || 'en-CA'

    return intlFormat(
      date,
      {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      },
      {
        locale: currentLocale,
      },
    )
  }

  const formatDistanceToNowLocalized = (date: Date, options?: { addSuffix?: boolean }) => {
    const currentLocale = localeMap[i18n.language] || enCA

    return formatDistanceToNow(date, {
      ...options,
      locale: currentLocale,
    })
  }

  const formatDateLong = (date: Date) => {
    const currentLocale = i18n.language || 'en-CA'

    return intlFormat(
      date,
      {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      },
      {
        locale: currentLocale,
      },
    )
  }

  const formatMonthYear = (date: Date) => {
    const currentLocale = i18n.language || 'en-CA'

    return intlFormat(
      date,
      {
        year: 'numeric',
        month: 'long',
      },
      {
        locale: currentLocale,
      },
    )
  }

  const formatMonth = (date: Date, options?: { month?: Intl.DateTimeFormatOptions['month'] }) => {
    const currentLocale = i18n.language || 'en-CA'

    return intlFormat(
      date,
      {
        month: options?.month ?? 'long',
      },
      {
        locale: currentLocale,
      },
    )
  }

  return {
    formatDate,
    formatDateLong,
    formatDateWithTime,
    formatDistanceToNowLocalized,
    formatHour,
    formatMonth,
    formatMonthYear,
  }
}
